import * as React from "react"
import { Head, usePage } from "@inertiajs/react"
import PublicLayout from "@/components/PublicLayout"
import { EnquiryFormSection } from "@/components/gisela/EnquiryForm"
import GiselaDoors, { type DoorItem, type TherapyCard } from "@/components/gisela/GiselaDoors"
import GiselaHomeHero from "@/components/gisela/GiselaHomeHero"
import GiselaPageTiles from "@/components/gisela/GiselaPageTiles"
import { TestimonialsBand } from "@/components/gisela/TestimonialsBand"
import GiselaPersonBlock from "@/components/gisela/GiselaPersonBlock"
import { type BaseBlockData } from "@/components/gisela/SanctuaryBaseBlock"
import WorkingTogether from "@/components/gisela/WorkingTogether"
import { HeroFilm, type HeroPhoto } from "@/components/gisela/HeroBand"
import { BrandMotif } from "@/components/ui/brand-motif"
import { useLocalePath } from "@/lib/locale-path"
import { presentBands } from "@/lib/section-order"
import { sectionHidden } from "@/lib/section-visibility"
import type { GiselaTherapist, PageMeta, PageProps, PlacementPhoto, PublicTestimonial } from "@/types/inertia"

interface Props {
  therapist: GiselaTherapist
  testimonials: PublicTestimonial[]
  // Present only when rendered as Gisela's home on her own host.
  base?: BaseBlockData | null
  // Hero photos, already cut to the admin's "how many / what order" settings.
  hero?: { slides: PlacementPhoto[]; interval_seconds: number }
  // The photograph the hero band sits on, under its film; null = plain teal.
  hero_background?: HeroPhoto | null
  tile_photos?: Record<string, PlacementPhoto | null>
  online_photo?: PlacementPhoto | null
  // The contact band's chosen background, as on her page heroes; null = plain teal.
  cta_photo?: HeroPhoto | null
  // The "How I work" cards under the doors — one per published therapy.
  therapies?: TherapyCard[]
  content: {
    hero: { intro_lead?: string; intro_sub?: string; _hidden?: boolean }
    person: { eyebrow?: string; heading?: string; lead?: string; body?: string }
    doors: DoorItem[]
    tiles: { _hidden?: boolean }
    working_together: {
      online_heading: string
      online_body: string
      in_person_body: string
      prestwich_body?: string
      hebden_bridge_body?: string
    }
    enquiry: { in_person_note: string; _hidden?: boolean }
  }
  /** Registry entry ids in display order — the admin's arrangement. */
  section_order?: string[]
  meta?: PageMeta
}

// Her home page, top to bottom: the hero (her photo + what she helps with),
// the founder band, the four "doors", where we meet, the contact band with
// its four tiles, and what clients say. The enquiry form lives on /contact;
// only the Sanctuary-host (pre-split) render keeps it inline.
export default function TherapyWithGisela({
  therapist,
  testimonials,
  base,
  hero = { slides: [], interval_seconds: 6 },
  hero_background = null,
  tile_photos = {},
  online_photo,
  cta_photo,
  therapies = [],
  content,
  section_order,
  meta,
}: Props) {
  const { props } = usePage<PageProps>()
  const tn = props.translations.gisela_nav
  const th = props.translations.gisela_pages.hero
  const tc = props.translations.gisela_pages.common
  // Booking CTAs only exist on Gisela's own host (this page also renders
  // pre-split on the Sanctuary host, where /book doesn't resolve).
  const onGiselaHost = props.current_brand.key === "gisela"
  const lp = useLocalePath()
  const title = meta?.title ?? "OCD, Anxiety & Trauma Therapist UK | CBT & EMDR Online"
  const description =
    meta?.description ??
    "BABCP-accredited CBT & EMDR therapist for OCD, anxiety and trauma — online UK-wide in English or Spanish, in person in Prestwich and Hebden Bridge."

  // Every block below the hero, named by its registry entry so the admin's
  // order applies. Blocks keep their own backgrounds; the enquiry form (the
  // Sanctuary-host render only) stays last.
  const bands = presentBands(
    [
      {
        id: "therapy-person",
        present: true,
        render: () => <GiselaPersonBlock therapist={therapist} content={content.person} />,
      },
      {
        id: "therapy-doors",
        present: true,
        render: () => <GiselaDoors doors={content.doors} therapies={therapies} />,
      },
      {
        id: "therapy-working-together",
        present: true,
        render: () => (
          <WorkingTogether
            content={content.working_together}
            base={onGiselaHost && !sectionHidden(base?.content) ? base : null}
            photo={online_photo}
          />
        ),
      },
      {
        id: "therapy-cta",
        present: true,
        render: () => (
          <section className="relative overflow-hidden bg-teal py-16 text-white">
            <HeroFilm photo={cta_photo} />
            <BrandMotif variant="rings" tone="white" size={720} opacity={0.35} className="absolute -top-80 -left-64" />
            <div className="relative mx-auto max-w-3xl px-6 text-center lg:px-8">
              <h2 className="text-white">{th.cta_heading}</h2>
              <p className="mt-3 text-white/75">{th.cta_body}</p>
              <div className="mt-7">
                {onGiselaHost ? (
                  <a
                    href={lp("/book")}
                    className="rounded-full bg-white px-6 py-2.5 text-sm font-medium text-teal no-underline shadow transition-opacity hover:opacity-90"
                  >
                    {tn.book_call}
                  </a>
                ) : (
                  <a
                    href="#enquiry-form"
                    className="rounded-full bg-white px-6 py-2.5 text-sm font-medium text-teal no-underline shadow transition-opacity hover:opacity-90"
                  >
                    {tc.send_enquiry}
                  </a>
                )}
              </div>
            </div>
          </section>
        ),
      },
      {
        id: "therapy-explore-tiles",
        present: !sectionHidden(content.tiles),
        // Every page of her site, six cards across two rows — the home page is
        // the one place none of them has to be left out.
        render: () => <GiselaPageTiles photos={tile_photos} />,
      },
      {
        id: "therapy-testimonials",
        present: props.features.testimonials_display,
        render: () => <TestimonialsBand testimonials={testimonials} />,
      },
    ],
    section_order,
  )

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        {hero_background?.url && (
          <link rel="preload" as="image" href={hero_background.url} fetchPriority="high" />
        )}
        {hero.slides[0] && <link rel="preload" as="image" href={hero.slides[0].url} fetchPriority="high" />}
      </Head>
      <PublicLayout darkHero={!sectionHidden(content.hero)}>
        {/* ── Hero: photo circle + what she helps with ───────────────── */}
        {!sectionHidden(content.hero) && (
          <GiselaHomeHero
            slides={hero.slides}
            intervalMs={hero.interval_seconds * 1000}
            background={hero_background}
            content={content.hero}
            therapistName={therapist.name}
          />
        )}

        {bands.map((band) => (
          <React.Fragment key={band.id}>{band.render(0)}</React.Fragment>
        ))}

        {/* ── Enquiry form ───────────────────────────────────────────────
            On Gisela's own host the form lives on /contact only — her home
            page sends people there. The Sanctuary-host (pre-split) render of
            this page has no /contact of Gisela's to point at, so it keeps the
            inline form. */}
        {!onGiselaHost && !sectionHidden(content.enquiry) && (
          <EnquiryFormSection inPersonNote={content.enquiry.in_person_note} />
        )}
      </PublicLayout>
    </>
  )
}
